/**
 * Subscription Tier Entitlements Library
 * 
 * Centralizes business logic for subscription tiers, exclusivity windows,
 * and lead routing eligibility.
 * 
 * Growth tier billing model (managed ad spend):
 *   - Provider tops up wallet (e.g. $100)
 *   - Platform charges a configurable fee on top of Meta spend
 *   - Fee rate is controlled by GROWTH_PLATFORM_FEE_RATE (or
 *     NEXT_PUBLIC_GROWTH_PLATFORM_FEE_RATE in Next.js contexts)
 *   - For every $X of Meta spend the fee = $X * r / (1 - r)
 *   - Total wallet burn = spend / (1 - r)
 *   - Campaigns pause when wallet < pauseThreshold(daily_budget, rate)
 * 
 * @module lib/entitlements
 */

export type SubscriptionTier = 'free' | 'starter' | 'growth';
export type QuoteOrigin = 'organic' | 'automated_ads' | 'broadcast';

/**
 * Parse and validate a raw GROWTH_PLATFORM_FEE_RATE string.
 *
 * Valid range: [0, 0.5). Values outside this range or non-numeric strings
 * fall back to 0 with a console.warn.
 *
 * @param raw - Value of the env var (e.g. '0.10', '0', undefined)
 * @returns A validated fee rate number in [0, 0.5)
 */
export function parseGrowthFeeRate(raw: string | undefined): number {
  if (raw === undefined || raw === '') return 0;
  const rate = parseFloat(raw);
  if (!Number.isFinite(rate) || rate < 0 || rate >= 0.5) {
    console.warn(`[growth] Invalid GROWTH_PLATFORM_FEE_RATE "${raw}", falling back to 0`);
    return 0;
  }
  return rate;
}

/**
 * Platform fee charged on a given Meta ad spend amount.
 *
 * For fee rate `r`: fee = round(spend * r / (1 - r), 2)
 * Total wallet burn = spend + fee = spend / (1 - r)
 *
 * Mirrors the Postgres helper `growth_platform_fee(numeric)` —
 * keep the two in sync.
 *
 * @param spend - Meta ad spend in USD (positive number)
 * @param rate  - Platform fee rate in [0, 0.5). Defaults to 0 (pilot-safe).
 *               Pass `parseGrowthFeeRate(process.env.GROWTH_PLATFORM_FEE_RATE)`
 *               in production callers.
 * @returns Platform fee in USD, rounded to cents (2 decimals)
 *
 * @example
 * feeFor(90, 0.10)  // 10       (90% spend / 10% fee split)
 * feeFor(90, 0)     // 0        (pilot: no fee)
 * feeFor(90, 0.15)  // 15.88
 */
export function feeFor(spend: number, rate: number = 0): number {
  if (!Number.isFinite(spend) || spend <= 0) return 0;
  if (rate === 0) return 0;
  return Math.round((spend * rate / (1 - rate)) * 100) / 100;
}

/**
 * Wallet balance threshold below which Growth campaigns must pause.
 *
 * Equals one day of total burn (`daily_budget / (1 - rate)`) plus a 15%
 * safety buffer to absorb Meta API reporting lag and intra-day overspend.
 *
 * Mirrors the Postgres helper `growth_pause_threshold(numeric)` —
 * keep the two in sync.
 *
 * @param dailyBudget - Daily ad budget in USD (positive number)
 * @param rate        - Platform fee rate in [0, 0.5). Defaults to 0 (pilot-safe).
 *                     Pass `parseGrowthFeeRate(process.env.GROWTH_PLATFORM_FEE_RATE)`
 *                     in production callers.
 * @returns Pause threshold in USD, rounded to cents (2 decimals)
 *
 * @example
 * pauseThreshold(50, 0.10) // 63.89  ->  50 / 0.9 * 1.15
 * pauseThreshold(50, 0)    // 57.50  ->  50 / 1.0 * 1.15 (pilot)
 * pauseThreshold(10, 0.10) // 12.78
 */
export function pauseThreshold(dailyBudget: number, rate: number = 0): number {
  if (!Number.isFinite(dailyBudget) || dailyBudget <= 0) return 0;
  return Math.round((dailyBudget / (1 - rate)) * 1.15 * 100) / 100;
}

export interface TierEntitlements {
  tier: SubscriptionTier;
  exclusivityWindowHours: number;
  canReceiveOrganicExclusive: boolean;
  canReceivePaidLeads: boolean;
  requiresWalletBalance: boolean;
}

/**
 * Maps raw subscription plan strings to canonical tier identifiers
 * 
 * Normalizes case-insensitive plan names from various sources:
 * - Clerk webhook: "Starter", "Growth", "Steady"
 * - Database: "starter", "growth", "free", "Free"
 * - Legacy: "Steady" (maps to growth)
 * 
 * @param subscriptionPlan - Raw subscription plan string from Clerk or database
 * @returns Normalized tier identifier
 * 
 * @example
 * normalizeTier('Growth') // 'growth'
 * normalizeTier('Starter') // 'starter'
 * normalizeTier('Steady') // 'growth' (legacy alias)
 * normalizeTier(null) // 'free'
 */
export function normalizeTier(
  subscriptionPlan: string | null | undefined
): SubscriptionTier {
  if (!subscriptionPlan) {
    return 'free';
  }

  const normalized = subscriptionPlan.toLowerCase().trim();

  switch (normalized) {
    case 'starter':
      return 'starter';
    case 'growth':
    case 'steady': // Legacy alias for Growth tier
      return 'growth';
    case 'free':
    default:
      return 'free';
  }
}

/**
 * Returns entitlement configuration for a subscription tier
 * 
 * Tier Features:
 * - **Free**: No exclusivity, broadcast only, no paid leads
 * - **Starter**: 24h exclusivity, organic exclusive, broadcast, no paid leads
 * - **Growth**: 48h exclusivity, organic exclusive, broadcast, paid leads (requires wallet)
 * 
 * @param subscriptionPlan - Raw subscription plan string
 * @returns Tier entitlements configuration
 * 
 * @example
 * const entitlements = getEntitlements('Growth');
 * // { tier: 'growth', exclusivityWindowHours: 48, canReceiveOrganicExclusive: true, ... }
 */
export function getEntitlements(
  subscriptionPlan: string | null | undefined
): TierEntitlements {
  const tier = normalizeTier(subscriptionPlan);

  switch (tier) {
    case 'starter':
      return {
        tier: 'starter',
        exclusivityWindowHours: 24,
        canReceiveOrganicExclusive: true,
        canReceivePaidLeads: false,
        requiresWalletBalance: false,
      };

    case 'growth':
      return {
        tier: 'growth',
        exclusivityWindowHours: 48,
        canReceiveOrganicExclusive: true,
        canReceivePaidLeads: true,
        requiresWalletBalance: true, // Growth ad-sourced leads require wallet > 0
      };

    case 'free':
    default:
      return {
        tier: 'free',
        exclusivityWindowHours: 0,
        canReceiveOrganicExclusive: false,
        canReceivePaidLeads: false,
        requiresWalletBalance: false,
      };
  }
}

/**
 * Checks if a business can receive a lead based on tier entitlements
 * 
 * Routing Rules:
 * - **broadcast**: All tiers can receive
 * - **organic**: Starter and Growth only (exclusive offers)
 * - **automated_ads**: Growth only, requires wallet > 0
 *   (campaign-level pause threshold is enforced separately by the
 *   ad-spend sync — see `pauseThreshold`)
 * 
 * @param subscriptionPlan - Business subscription plan
 * @param origin - Lead acquisition channel
 * @param walletBalance - Optional wallet balance (required for automated_ads check)
 * @returns true if business is eligible to receive this lead
 * 
 * @example
 * canReceiveLead('Starter', 'broadcast') // true
 * canReceiveLead('Starter', 'automated_ads') // false
 * canReceiveLead('Growth', 'automated_ads', 10) // true
 * canReceiveLead('Growth', 'automated_ads', 0) // false (no wallet balance)
 * canReceiveLead('Free', 'broadcast') // true
 * canReceiveLead('Free', 'organic') // false (no exclusivity for free tier)
 */
export function canReceiveLead(
  subscriptionPlan: string | null | undefined,
  origin: QuoteOrigin,
  walletBalance?: number
): boolean {
  const entitlements = getEntitlements(subscriptionPlan);

  switch (origin) {
    case 'broadcast':
      // All tiers can receive broadcast leads
      return true;

    case 'organic':
      // Only Starter and Growth can receive exclusive organic leads
      return entitlements.canReceiveOrganicExclusive;

    case 'automated_ads':
      // Only Growth tier can receive paid leads
      if (!entitlements.canReceivePaidLeads) {
        return false;
      }

      // Growth tier requires positive wallet balance to receive ad-sourced leads.
      // Per-campaign pause logic (15% buffer) is enforced upstream when spend is
      // synced; this is the per-lead floodgate.
      if (entitlements.requiresWalletBalance) {
        return (walletBalance ?? 0) > 0;
      }

      return true;

    default:
      // Unknown origin - deny by default
      return false;
  }
}

/**
 * Calculates exclusivity expiry timestamp for a subscription tier
 * 
 * - **Free**: No exclusivity (returns null)
 * - **Starter**: 24 hours from now
 * - **Growth**: 48 hours from now
 * 
 * @param subscriptionPlan - Business subscription plan
 * @param fromDate - Optional base date (defaults to now)
 * @returns Expiry date or null if no exclusivity
 * 
 * @example
 * const expiry = calculateExclusivityExpiry('Starter');
 * // Date 24 hours from now
 * 
 * const growthExpiry = calculateExclusivityExpiry('Growth', new Date('2026-02-12T00:00:00Z'));
 * // Date('2026-02-14T00:00:00Z') - 48 hours later
 * 
 * const freeExpiry = calculateExclusivityExpiry('Free');
 * // null (no exclusivity for free tier)
 */
export function calculateExclusivityExpiry(
  subscriptionPlan: string | null | undefined,
  fromDate?: Date
): Date | null {
  const entitlements = getEntitlements(subscriptionPlan);

  if (entitlements.exclusivityWindowHours === 0) {
    return null;
  }

  const baseDate = fromDate ?? new Date();
  const expiryDate = new Date(baseDate);
  expiryDate.setHours(
    expiryDate.getHours() + entitlements.exclusivityWindowHours
  );

  return expiryDate;
}
